import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { comics, testimonials, categories } from "@/mocks/comics";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const comic = comics.find((c) => c.slug === slug);
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const shareRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (shareRef.current && !shareRef.current.contains(e.target as Node)) {
        setShowShareMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!comic) {
    return (
      <>
        <Navbar />
        <main className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-emotion-sad-line text-2xl text-foreground-600"></i>
            </div>
            <h1 className="text-xl font-semibold text-foreground-950 mb-2">漫画未找到</h1>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 transition-colors">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedComics = comics
    .filter((c) => c.id !== comic.id && c.category === comic.category)
    .slice(0, 3);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: `${comic.title} - 韩漫画网无删减下拉式在线`,
    description: comic.description,
    author: { "@type": "Person", name: comic.author },
    datePublished: "2025-01-01",
    dateModified: comic.updateDate,
    image: comic.cover,
    publisher: {
      "@type": "Organization",
      name: "韩漫画网",
    },
    mainEntityOfPage: `http://xxjiaxiang.com/article/${slug}`,
  };

  return (
    <>
      <Navbar />

      <main>
        {/* Breadcrumb */}
        <nav className="pt-20 pb-0" aria-label="面包屑导航">
          <div className="max-w-[1200px] mx-auto px-4 md:px-6 py-4">
            <ol className="flex items-center gap-2 text-xs text-foreground-600">
              <li><Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link></li>
              <li><span className="text-foreground-400">/</span></li>
              <li><Link to={`/category/${encodeURIComponent(categories.find(c => c.name === comic.category)?.slug || "")}`} className="hover:text-primary-500 transition-colors cursor-pointer">{comic.category}</Link></li>
              <li><span className="text-foreground-400">/</span></li>
              <li><span className="text-foreground-950 font-medium">{comic.title}</span></li>
            </ol>
          </div>
        </nav>

        {/* Article Header - About Section */}
        <section className="bg-background-50 py-10" aria-labelledby="article-title">
          <div className="max-w-[1200px] mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row gap-10">
              {/* Left - Info */}
              <div className="lg:w-[55%]">
                <h1 id="article-title" className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4" style={{ fontFamily: "var(--font-heading)" }}>
                  {comic.title}
                </h1>
                <p className="text-sm text-foreground-600 mb-2">{comic.altTitle}</p>
                <p className="text-base text-foreground-700 leading-relaxed mb-6 max-w-xl">
                  {comic.description}
                </p>

                <button className="inline-flex items-center gap-2 px-6 py-2.5 bg-accent-500/10 text-accent-700 rounded-full text-sm font-medium hover:bg-accent-500/20 transition-colors whitespace-nowrap cursor-pointer">
                  开始阅读
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line text-sm"></i>
                  </div>
                </button>

                <div className="flex items-center gap-12 mt-8">
                  <div>
                    <div className="text-3xl font-bold text-foreground-950" style={{ fontFamily: "var(--font-heading)" }}>
                      {comic.chapters}
                    </div>
                    <div className="text-xs text-foreground-600 mt-1">总章节</div>
                  </div>
                  <div>
                    <div className="text-3xl font-bold text-foreground-950" style={{ fontFamily: "var(--font-heading)" }}>
                      {(comic.views / 10000).toFixed(0)}万
                    </div>
                    <div className="text-xs text-foreground-600 mt-1">阅读量</div>
                  </div>
                </div>

                <div className="flex items-center gap-3 mt-6">
                  <button
                    onClick={() => setIsBookmarked(!isBookmarked)}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      isBookmarked ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-600 hover:bg-background-200"
                    }`}
                  >
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className={`${isBookmarked ? "ri-bookmark-fill" : "ri-bookmark-line"} text-sm`}></i>
                    </div>
                    {isBookmarked ? "已收藏" : "收藏"}
                  </button>
                  <div className="relative" ref={shareRef}>
                    <button
                      onClick={() => setShowShareMenu(!showShareMenu)}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium bg-background-100 text-foreground-600 hover:bg-background-200 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-share-line text-sm"></i>
                      </div>
                      分享
                    </button>
                    {showShareMenu && (
                      <div className="absolute top-full left-0 mt-2 bg-background-50 border border-background-200/70 rounded-lg shadow-sm p-2 min-w-[160px] z-20">
                        <button className="w-full text-left px-3 py-2 text-sm text-foreground-700 hover:bg-background-100 rounded-md transition-colors cursor-pointer">复制链接</button>
                        <button className="w-full text-left px-3 py-2 text-sm text-foreground-700 hover:bg-background-100 rounded-md transition-colors cursor-pointer">分享到微博</button>
                        <button className="w-full text-left px-3 py-2 text-sm text-foreground-700 hover:bg-background-100 rounded-md transition-colors cursor-pointer">分享到微信</button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Right - Cover */}
              <div className="lg:w-[45%] flex justify-center lg:justify-end">
                <div className="relative w-[280px] h-[392px] rounded-lg overflow-hidden">
                  <img
                    src={comic.cover}
                    alt={`${comic.title} - 韩漫画网无删减下拉式在线阅读`}
                    title={`${comic.title} - 韩漫大全下拉式免费`}
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute top-4 left-4">
                    <span className={`px-3 py-1.5 rounded-full text-xs font-semibold bg-background-50/90 backdrop-blur-sm ${comic.status === "连载中" ? "text-primary-500" : "text-foreground-700"}`}>
                      {comic.status}
                    </span>
                  </div>
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-4 pt-12">
                    <p className="text-white text-sm font-medium">{comic.author}</p>
                    <p className="text-white/70 text-xs">最近更新：{comic.updateDate}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Scroll Reading Area */}
        <section className="bg-background-100/70 py-14" aria-label="漫画阅读区域">
          <div className="max-w-[800px] mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-lg font-semibold text-foreground-950 mb-2">下拉式阅读体验</h2>
              <p className="text-sm text-foreground-600">向下滚动即可连续阅读，无需翻页</p>
            </div>

            {/* Mock reading panels */}
            <div className="space-y-4">
              {[
                "https://readdy.ai/api/search-image?query=Korean%20webtoon%20comic%20panel%20dramatic%20opening%20scene%2C%20character%20introduction%20with%20dialogue%20bubbles%2C%20clean%20line%20art%2C%20vibrant%20colors%2C%20professional%20manhwa%20illustration%20style&width=1200&height=800&seq=comic-panel-read-1&orientation=landscape",
                "https://readdy.ai/api/search-image?query=Korean%20webtoon%20action%20scene%20with%20two%20characters%20fighting%2C%20dynamic%20motion%20lines%2C%20speed%20effects%2C%20dramatic%20lighting%2C%20clean%20inking%2C%20vibrant%20manhwa%20style&width=1200&height=800&seq=comic-panel-read-2&orientation=landscape",
                "https://readdy.ai/api/search-image?query=Korean%20webtoon%20emotional%20dialogue%20scene%2C%20close-up%20character%20faces%20with%20expressive%20eyes%2C%20soft%20background%20blur%2C%20dramatic%20tension%2C%20clean%20line%20art%2C%20professional%20manhwa&width=1200&height=800&seq=comic-panel-read-3&orientation=landscape",
                "https://readdy.ai/api/search-image?query=Korean%20webtoon%20epic%20reveal%20moment%2C%20character%20transformation%20with%20glowing%20energy%20effects%2C%20dramatic%20lighting%20from%20above%2C%20cinematic%20composition%2C%20professional%20manhwa%20illustration&width=1200&height=800&seq=comic-panel-read-4&orientation=landscape",
                "https://readdy.ai/api/search-image?query=Korean%20webtoon%20cliffhanger%20ending%20panel%2C%20mysterious%20silhouette%20appearing%2C%20dark%20atmosphere%2C%20dramatic%20shadows%2C%20clean%20line%20art%20style%2C%20professional%20manhwa%20illustration&width=1200&height=800&seq=comic-panel-read-5&orientation=landscape",
              ].map((url, idx) => (
                <div key={idx} className="bg-background-50 rounded-lg overflow-hidden">
                  <img
                    src={url}
                    alt={`${comic.title} 第${idx + 1}页`}
                    className="w-full object-cover"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>

            {/* Chapter Navigation */}
            <div className="flex items-center justify-between mt-8 pt-6 border-t border-background-200/70">
              <button className="inline-flex items-center gap-2 px-4 py-2 text-sm text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-left-line text-sm"></i>
                </div>
                上一话
              </button>
              <span className="text-sm text-foreground-600">第 1 话</span>
              <button className="inline-flex items-center gap-2 px-4 py-2 text-sm text-foreground-600 hover:text-primary-500 transition-colors cursor-pointer">
                下一话
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-sm"></i>
                </div>
              </button>
            </div>
          </div>
        </section>

        {/* Comments Section */}
        <section className="bg-background-50 py-14" aria-labelledby="comments-heading">
          <div className="max-w-[1200px] mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <span className="inline-block w-2 h-2 rounded-full bg-accent-500 mr-2 align-middle"></span>
              <span className="text-xs font-semibold text-foreground-600 uppercase tracking-wide">读者评论</span>
              <h2 id="comments-heading" className="text-2xl md:text-3xl font-bold text-foreground-950 mt-2" style={{ fontFamily: "var(--font-heading)" }}>
                大家怎么说
              </h2>
              <p className="text-sm text-foreground-600 mt-3">看看其他读者的评价</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {testimonials.map((t, idx) => (
                <div
                  key={t.id}
                  className={`bg-background-100 rounded-lg p-5 ${idx === testimonials.length - 1 ? "opacity-60" : ""}`}
                >
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <div key={i} className="w-4 h-4 flex items-center justify-center">
                        <i className={`text-sm ${i < t.rating ? "ri-star-fill text-accent-500" : "ri-star-line text-foreground-400"}`}></i>
                      </div>
                    ))}
                  </div>
                  <h4 className="text-sm font-semibold text-foreground-950 mb-2">好评推荐</h4>
                  <p className="text-xs text-foreground-600 leading-relaxed mb-4">{t.content}</p>
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full overflow-hidden bg-background-200">
                      <img src={t.avatar} alt={t.name} className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-foreground-950">{t.name}</p>
                      <p className="text-[10px] text-foreground-600">{t.date}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Related Comics */}
        {relatedComics.length > 0 && (
          <section className="bg-background-100/70 py-14" aria-labelledby="related-heading">
            <div className="max-w-[1200px] mx-auto px-4 md:px-6">
              <h2 id="related-heading" className="text-xl font-bold text-foreground-950 mb-8" style={{ fontFamily: "var(--font-heading)" }}>
                猜你喜欢
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6" data-product-shop="">
                {relatedComics.map((c) => (
                  <Link key={c.id} to={`/article/${c.slug}`} className="group cursor-pointer">
                    <div className="bg-background-50 rounded-lg overflow-hidden transition-all duration-300 hover:scale-[1.02]">
                      <div className="relative h-[240px] overflow-hidden">
                        <img
                          src={c.cover}
                          alt={`${c.title} - 韩漫画网`}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-3">
                        <h4 className="text-sm font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors" style={{ fontFamily: "var(--font-heading)" }}>
                          {c.title}
                        </h4>
                        <p className="text-xs text-foreground-600 mt-1">{c.author}</p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />

      <script type="application/ld+json">
        {JSON.stringify(articleSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: "http://xxjiaxiang.com/" },
            { "@type": "ListItem", position: 2, name: comic.category, item: `http://xxjiaxiang.com/category/${encodeURIComponent(categories?.find(c => c.name === comic.category)?.slug || "")}` },
            { "@type": "ListItem", position: 3, name: comic.title, item: `http://xxjiaxiang.com/article/${slug}` },
          ],
        })}
      </script>
    </>
  );
}